import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import useLobbyingMaterial from 'api/hooks/useLobbyingMaterial';
import type { TeamLobbyingMaterialItem } from 'api/hooks/useLobbyingMaterial';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import { lobbyingMaterialEditPath } from 'components/UploadHub/UploadHubPreviewPage/lobbyingMaterialEditPath';

const AddToLobbyingMaterialButton = ({
  setIsOpenAddLobbyingMaterial,
  setIsMoreActionsOpen,
}: {
  setIsOpenAddLobbyingMaterial: (value: boolean) => void;
  setIsMoreActionsOpen: (value: boolean) => void;
}) => {
  return (
    <>
      <button
        className='w-100 d-flex align-items-center text-left general-button list-action-button specific-right-padding-28 pl-3 py-2'
        onClick={() => {
          setIsOpenAddLobbyingMaterial(true);
          setIsMoreActionsOpen(false);
        }}
      >
        <span
          className='political-updates-nav-icon specific-left-margin-2 icon-briefing-material paragraph-p1 d-inline-block-centered specific-top-margin-1'
          style={{ marginRight: '-2px' }}
        />{' '}
        <span>Add to lobbying material</span>
      </button>
    </>
  );
};

interface ModalContentProps {
  file: UploadHubListItem;
  openState: { isOpen: boolean; setIsOpen: (value: boolean) => void };
}

const AddToLobbyingMaterialPopup = ({ file, openState }: ModalContentProps) => {
  const { isOpen, setIsOpen } = openState;
  const fileName = file?.fileName ?? '';
  const navigate = useNavigate();

  const savedFiles = [{ url: file.url, name: file.fileName }];

  const { logEvent } = useUploadFiles();
  const logAddedToLobbyingMaterial = (targetId?: number) =>
    logEvent({
      eventName: 'UploadHubFileAddedToLobbyingMaterial',
      uploadHubItemId: file.id,
      targetId,
      isNewTarget: targetId === undefined,
    });

  const onConfirmNew = () => {
    logAddedToLobbyingMaterial();
    navigate(
      { pathname: '/brief-policymakers/add-content', search: '?step=1' },
      { state: { headline: file.fileName, attachments: { savedFiles } } }
    );
  };

  const onConfirmExisting = (item: TeamLobbyingMaterialItem) => {
    logAddedToLobbyingMaterial(item.id);
    navigate(lobbyingMaterialEditPath(item.type, item.id), { state: { attachments: { savedFiles } } });
  };

  const { getTeamLobbyingMaterial } = useLobbyingMaterial();
  const [option, setOption] = useState<'new' | 'existing'>('new');
  const [items, setItems] = useState<TeamLobbyingMaterialItem[]>([]);
  const [selected, setSelected] = useState<TeamLobbyingMaterialItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchItems = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchItems.current = async (signal) => {
    try {
      const results = await getTeamLobbyingMaterial({ signal });
      if (results) {
        setItems(results);
        setSelected(results[0] ?? null);
      }
      setIsLoading(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    fetchItems.current?.(controller.signal);
    return () => controller.abort('Lobbying material list cancelled.');
  }, [isOpen]);

  const confirm = () => {
    if (option === 'new') onConfirmNew();
    else if (selected) onConfirmExisting(selected);
    setIsOpen(false);
  };

  return (
    <>
      <ModalComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        overlayClassName='react-modal-overlay adjustment-of-position'
      >
        <h3 className='p-4 border-bottom title-h4-bold mb-0'>Add “{fileName}” to lobbying material</h3>
        <div className='p-4'>
          <div className='d-md-flex align-items-center gap-10 mb-4'>
            <button
              className={`w-100 mb-4 mb-lg-0 text-left general-button col-md border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                option === 'new' ? 'selected' : ''
              }`}
              onClick={() => setOption('new')}
            >
              <span className='select-item d-inline-block-centered mr-2' />
              New lobbying material
            </button>
            <button
              className={`w-100 text-left general-button col-md border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
                option === 'existing' ? 'selected' : ''
              }`}
              onClick={() => setOption('existing')}
            >
              <span className='select-item d-inline-block-centered mr-2' />
              Existing lobbying material
            </button>
          </div>

          {option === 'existing' && (
            <div className='mb-4'>
              <DropdownComponent
                mainItem={() => (
                  <span className='specific-y-padding-5 d-inline-block' style={{ maxHeight: '47px' }}>
                    {isLoading ? 'Loading…' : selected ? selected.title : 'Select a lobbying material'}
                  </span>
                )}
              >
                <CustomScrollbar
                  className='simple-scrollbar'
                  autoHeight
                  autoHeightMin='100%'
                  autoHeightMax='226px'
                  maximalThumbYSize={100}
                >
                  {isLoading ? (
                    <div className='p-3 light-60-text font-italic'>Loading…</div>
                  ) : items.length === 0 ? (
                    <div className='p-3 light-60-text font-italic'>You have not created any lobbying material yet</div>
                  ) : (
                    <ul className='policy-list px-0 py-1 mb-0'>
                      {items.map((item) => (
                        <li
                          key={`lobbying-material-${item.id}`}
                          onClick={() => setSelected(item)}
                          className='flex-centered specific-y-padding-8 px-3 list-action-button pointer'
                        >
                          <div className='flex-grow-1 flex-centered'>
                            <span>{item.title}</span>
                            {item?.publishedAt === null && (
                              <span className='ml-2 border rounded-pill paragraph-p3 light-60-text px-2 specific-bottom-padding-1'>
                                Draft
                              </span>
                            )}
                          </div>
                          {selected?.id === item.id && <span className='icon-tick paragraph-p4' />}
                        </li>
                      ))}
                    </ul>
                  )}
                </CustomScrollbar>
              </DropdownComponent>
            </div>
          )}

          <div className='pt-3 d-flex justify-content-end'>
            <button
              onClick={confirm}
              disabled={option === 'existing' && !selected}
              className='general-button rounded-pill action-button px-5 specific-y-padding-8'
            >
              {option === 'new' ? 'Create lobbying material' : 'Add to lobbying material'}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export { AddToLobbyingMaterialPopup };
export default AddToLobbyingMaterialButton;
